import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { HomeVehicleSection } from '../components/home/HomeVehicleSection'
import { useRouteMetadata } from '../components/inventory/useRouteMetadata'
import { companyManifest } from '../data/company'
import { translateHome } from '../components/home/messages'
import { getLocalizedPath } from '../i18n/routes'
import { useI18n } from '../i18n/I18nProvider'
import { Link } from '../ui/Link'
import { CommercialLink } from '../ui/CommercialLink'
import { VerificationMark } from '../ui/VerificationMark'
import '../components/home/home.css'

export function HomePage() {
  const { language } = useI18n()
  const { pathname } = useLocation()
  const t = useCallback(
    (key: Parameters<typeof translateHome>[1], values?: Record<string, string | number>) =>
      translateHome(language, key, values),
    [language],
  )
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    '@id': 'https://ts-auto.it/#dealer',
    name: companyManifest.legalName.value,
    description: companyManifest.business.value,
    areaServed: companyManifest.location.value,
    url: 'https://ts-auto.it/it',
  }), [])

  useRouteMetadata({
    title: t('metadataTitle'),
    description: t('metadataDescription'),
    pathname,
    language,
    structuredData,
  })

  return (
    <div className="home-page">
      <div className="home-hero">
        <picture>
          <source media="(max-width: 47.9375rem)" srcSet="/media/hero/hero-mobile.webp" />
          <img
            src="/media/hero/hero-desktop.webp"
            alt={t('heroImageAlt')}
            width="1672"
            height="941"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
      </div>

      <HomeVehicleSection />

      <section className="home-trust" aria-labelledby="home-trust-title">
        <div className="page-frame home-trust__grid">
          <div className="home-trust__copy">
            <h2 id="home-trust-title">{t('trustTitle')}</h2>
            <p>{t('trustBody')}</p>
          </div>
          <ul className="home-trust__proofs">
            <li><VerificationMark label={t('trustMileage')} /></li>
            <li><VerificationMark label={t('trustWarranty')} /></li>
            <li><VerificationMark label={t('trustDocumentation')} /></li>
          </ul>
        </div>
      </section>

      <section className="home-journey" aria-labelledby="home-journey-title">
        <div className="page-frame home-journey__grid">
          <header className="home-journey__heading">
            <h2 id="home-journey-title">{t('journeyTitle')}</h2>
            <p>{t('journeyIntro')}</p>
          </header>
          <article className="home-journey__path home-journey__path--buy">
            <h3>{t('journeyBuyTitle')}</h3>
            <p>{t('journeyBuyBody')}</p>
            <Link variant="text" to={getLocalizedPath('vehicles', language)}>{t('journeyBuyAction')}</Link>
          </article>
          <article className="home-journey__path home-journey__path--sell">
            <h3>{t('journeySellTitle')}</h3>
            <p>{t('journeySellBody')}</p>
            <Link variant="text" to={getLocalizedPath('sellVehicle', language)}>{t('journeySellAction')}</Link>
          </article>
        </div>
      </section>

      <section className="home-services" aria-labelledby="home-services-title">
        <div className="page-frame home-services__grid">
          <div className="home-services__intro">
            <h2 id="home-services-title">{t('servicesTitle')}</h2>
            <p>{t('servicesBody')}</p>
            <Link variant="secondary" to={getLocalizedPath('services', language)}>{t('servicesAction')}</Link>
          </div>
          <div className="home-services__list">
            <article><h3>{t('servicesFinanceTitle')}</h3><p>{t('servicesFinanceBody')}</p></article>
            <article><h3>{t('servicesRentalTitle')}</h3><p>{t('servicesRentalBody')}</p></article>
            <article><h3>{t('servicesSupportTitle')}</h3><p>{t('servicesSupportBody')}</p></article>
          </div>
        </div>
      </section>

      <section className="home-contact commercial-cta" aria-labelledby="home-contact-title">
        <div className="page-frame home-contact__grid">
          <div>
            <h2 id="home-contact-title">{t('contactTitle')}</h2>
          </div>
          <div className="home-contact__action">
            <p>{t('contactBody')}</p>
            <CommercialLink to={getLocalizedPath('contacts', language)}>{t('contactAction')}</CommercialLink>
          </div>
        </div>
      </section>
    </div>
  )
}
